import { useState } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Search, Check } from 'lucide-react';
import { countriesSorted } from '@/data/countries';
import { useScrollToActivate } from '@/hooks/useScrollToActivate';
import { FlagImg } from '@/components/ui/CountryFlag';

export function PaymentDestinationsStep() {
  const { goToNextStep, updateData, data } = useOnboarding();
  const { canActivate } = useScrollToActivate();
  // Initialize from context to preserve data on back navigation
  const [selectedCountries, setSelectedCountries] = useState<string[]>(() => 
    data.paymentDestinations?.map(d => d.code) || []
  );
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCountries = countriesSorted.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleCountry = (code: string) => {
    if (selectedCountries.includes(code)) {
      setSelectedCountries(selectedCountries.filter((c) => c !== code));
    } else if (selectedCountries.length < 3) {
      setSelectedCountries([...selectedCountries, code]);
      setSearchQuery(''); // Clear search after selecting a country
    }
  };

  const handleContinue = () => {
    const selected = selectedCountries.map((code) =>
      countriesSorted.find((c) => c.code === code)
    );
    updateData({
      paymentDestinations: selected as { code: string; name: string; flag: string }[],
    });
    goToNextStep();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col pt-24 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full overflow-y-auto">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
          <Send className="w-8 h-8 text-accent" />
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-3">
          Payment Destinations
        </h1>
        
        <p className="text-muted-foreground mb-2">
          Where do you normally send payments?
        </p>
        <p className="text-sm text-muted-foreground mb-6">
          Select up to 3 countries ({selectedCountries.length}/3)
        </p>

        {selectedCountries.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedCountries.map((code) => {
              const country = countriesSorted.find((c) => c.code === code);
              return (
                <button
                  key={code}
                  onClick={() => toggleCountry(code)}
                  className="flex items-center gap-2 px-3 py-2 bg-accent/20 border border-accent rounded-full"
                >
                  <FlagImg code={country.code ?? ''} size="sm" />
                  <span className="text-sm font-medium text-foreground">{country?.name}</span>
                  <Check className="w-4 h-4 text-accent" />
                </button>
              );
            })}
          </div>
        )}

        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Start typing to search countries..."
            className="pl-12 h-14 bg-muted/30 border-border rounded-xl"
          />
        </div>

        {searchQuery.length > 0 && (
          <div className="flex-1 overflow-y-auto -mx-2 px-2 max-h-64">
            <div className="grid grid-cols-1 gap-2">
              {filteredCountries.map((country) => {
                const isSelected = selectedCountries.includes(country.code);
                const isDisabled = !isSelected && selectedCountries.length >= 3;
                
                return (
                  <button
                    key={country.code}
                    onClick={() => !isDisabled && toggleCountry(country.code)}
                    disabled={isDisabled}
                    className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                      isSelected
                        ? 'border-accent bg-accent/10'
                        : isDisabled
                        ? 'border-border bg-muted/10 opacity-50 cursor-not-allowed'
                        : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                    }`}
                  >
                    <FlagImg code={country.code} size="lg" />
                    <span className="font-medium text-foreground flex-1 text-left">{country.name}</span>
                    {isSelected && (
                      <Check className="w-5 h-5 text-accent" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

      </div>
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
        <div className="max-w-md mx-auto">
          <Button
            onClick={handleContinue}
            disabled={selectedCountries.length === 0 || !canActivate}
            className="w-full h-14 text-lg font-semibold rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
